import { useEffect, useMemo, useState } from "react";
import { Award, Search, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { TIERS } from "@/lib/honor";
import { UserAvatar } from "@/components/UserBits";
import { TierPicker } from "@/components/admin/HonorTierPicker";
import { useHonorSave } from "@/components/admin/HonorAdminCard";

const Assign = ({ u, onDone }) => {
  const [tier, setTier] = useState("gold");
  const [title, setTitle] = useState("");
  const [save, busy] = useHonorSave(u.user_id, u.name, onDone);
  return (
    <div className="space-y-3 pt-3" data-testid={`honor-assign-${u.user_id}`}>
      <TierPicker value={tier} onChange={setTier} disabled={busy} testId="honor-assign-tier" />
      <div className="flex gap-2">
        <input value={title} maxLength={60} onChange={(e) => setTitle(e.target.value)} placeholder="Ehrentitel (optional)" data-testid="honor-assign-title"
          className="flex-1 min-w-0 h-11 px-4 rounded-full border border-pink-100 text-sm focus:outline-none focus:ring-2 focus:ring-rose-200" />
        <button onClick={() => save(true, title, tier)} disabled={busy} data-testid="honor-assign-save" className={`tier-${tier} honor-chip h-11 px-5 rounded-full text-sm font-extrabold`}>Ernennen ✨</button>
      </div>
    </div>
  );
};

const Member = ({ m, onChanged }) => {
  const [save, busy] = useHonorSave(m.user_id, m.name, onChanged);
  const T = TIERS[m.tier];
  return (
    <li className={`tier-${m.tier} honor-card rounded-3xl p-4 space-y-3`} data-testid={`honor-admin-member-${m.user_id}`}>
      <div className="flex items-center gap-3">
        <UserAvatar userId={m.user_id} name={m.name} picture={m.picture} size="w-12 h-12" noStory />
        <div className="min-w-0 flex-1">
          <p className="font-extrabold honor-name truncate">{m.name}</p>
          <p className="text-xs honor-title font-bold flex items-center gap-1"><T.icon className="w-3.5 h-3.5" /> {T.label}{m.title && ` · ${m.title}`}</p>
        </div>
        <button onClick={() => window.confirm(`${m.name} den Ehrenstatus entziehen?`) && save(false, m.title, m.tier)} disabled={busy} aria-label="Ehrenstatus entfernen" data-testid={`honor-admin-remove-${m.user_id}`}
          className="w-9 h-9 rounded-full bg-white/80 text-slate-400 hover:text-rose-600 flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
      </div>
      <TierPicker value={m.tier} disabled={busy} onChange={(t) => save(true, m.title, t)} testId={`honor-admin-tier-${m.user_id}`} />
    </li>
  );
};

export function HonorAdmin() {
  const [list, setList] = useState([]);
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");
  const [pick, setPick] = useState(null);
  const load = () => api.get("/admin/honor").then(({ data }) => setList(data)).catch(() => {});
  useEffect(() => { load(); api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => setUsers([])); }, []);
  const hits = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (s.length < 2 || !users) return [];
    const taken = new Set(list.map((m) => m.user_id));
    return users.filter((u) => !taken.has(u.user_id) && (u.name?.toLowerCase().includes(s) || u.email?.toLowerCase().includes(s))).slice(0, 8);
  }, [q, users, list]);
  const done = () => { setPick(null); setQ(""); load(); };
  return (
    <div className="space-y-6 max-w-3xl" data-testid="honor-admin">
      <div className="tier-gold honor-card rounded-3xl p-5 space-y-3">
        <p className="font-extrabold flex items-center gap-2"><Award className="w-5 h-5 text-[var(--h3)]" /> Neues Ehrenmitglied ernennen</p>
        <label className="relative block">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input value={q} onChange={(e) => { setQ(e.target.value); setPick(null); }} placeholder="Name oder E-Mail suchen …" data-testid="honor-admin-search"
            className="w-full h-12 pl-11 pr-4 rounded-full border border-pink-100 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-rose-200" />
        </label>
        {hits.length > 0 && !pick && (
          <ul className="bg-white rounded-2xl border border-pink-100 divide-y divide-pink-50 overflow-hidden" data-testid="honor-admin-hits">
            {hits.map((u) => (
              <li key={u.user_id}><button onClick={() => setPick(u)} data-testid={`honor-admin-hit-${u.user_id}`} className="w-full flex items-center gap-3 p-3 text-left hover:bg-rose-50">
                <UserAvatar userId={u.user_id} name={u.name} picture={u.picture} size="w-9 h-9" noStory />
                <span className="min-w-0"><span className="block text-sm font-bold truncate">{u.name}</span><span className="block text-xs text-slate-400 truncate">{u.email}</span></span>
              </button></li>
            ))}
          </ul>
        )}
        {pick && <><p className="text-sm">Ausgewählt: <b>{pick.name}</b> – Stufe wählen:</p><Assign key={pick.user_id} u={pick} onDone={done} /></>}
      </div>
      <div className="space-y-3">
        <p className="font-extrabold">Alle Ehrenmitglieder ({list.length})</p>
        {list.length === 0 ? <p className="text-sm text-slate-400" data-testid="honor-admin-empty">Noch keine Ehrenmitglieder.</p>
          : <ul className="grid sm:grid-cols-2 gap-3">{list.map((m) => <Member key={m.user_id} m={m} onChanged={load} />)}</ul>}
      </div>
    </div>
  );
}
